import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Stack, Typography } from '@packages/shared';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ServiceLayer } from '@packages/modules';
import { useTracking } from '@packages/tracking';
import { TextWithDialog } from '../TextWithDialog';
import { messages } from './messages';

const CarrierDeliveryInfoFragment = graphql(/* GraphQL */ `
  fragment CarrierDeliveryInfoFragment on Delivery {
    deliverySize
  }
`);

export type CarrierDeliveryInfoProps = {
  deliveryData: FragmentType<typeof CarrierDeliveryInfoFragment>;
};

/**
 * <p>
 * The <code>CarrierDeliveryInfo</code> component is used to display all available carrier delivery messages.
 * Therefore it iterates over the <code>deliveryData</code> values and returns the corresponding info text.
 * </p>
 * <p>
 * As of right now there are 2 sizes which are being delivery by a carrier:
 * <ul>
 *  <li>S - articles are delivered into home</li>
 *  <li>L - articles are delivered to property boundary</li>
 * </ul>
 * </p>
 * <p>
 * The content of the dialog is being loaded from the Inspire CMS.
 * </p>
 * */

export const CarrierDeliveryInfo = ({ deliveryData }: CarrierDeliveryInfoProps) => {
  const { deliverySize } = getFragmentData(CarrierDeliveryInfoFragment, deliveryData);
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();

  const onClickEventHandler = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'noteLayer',
      eventLabel: 'detailview',
      eventValue: formatMessage(messages.dialogHeadline).concat(' ', deliverySize),
    });
  };

  const messageValues = {
    link: (msg: ReactNode) => (
      <TextWithDialog
        message={msg}
        dialogTitle={formatMessage(messages.dialogHeadline)}
        dialogContent={<ServiceLayer serviceLayerId={`delivery_spedition-layer-${deliverySize}`} />}
        onClickHandler={onClickEventHandler}
        variant="body2"
        displayTrackingProps={{ category: 'dv-service-layer-carrier-delivery-info' }}
      />
    ),
  };

  return (
    <Stack spacing={0.5}>
      {deliverySize === 'S' && (
        <Typography variant="body2">
          {formatMessage(messages.carrierDeliveryS, messageValues)}
        </Typography>
      )}
      {deliverySize === 'L' && (
        <Typography variant="body2">
          {formatMessage(messages.carrierDeliveryL, messageValues)}
        </Typography>
      )}
    </Stack>
  );
};
